import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatDateTime } from '../utils/format';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';
import AutoRefreshControls from '../components/AutoRefreshControls';

const SOURCES = [
  { key: 'sg_pools', permission: '/errors/sg-pools', listPath: '/error/sg_pools', screenshotPath: '/error/sg_pools/screen_shot', labelKey: 'errors.tabSgPools' },
  { key: 'now_goal', permission: '/errors/now-goal', listPath: '/error/now_goal', screenshotPath: '/error/now_goal/screen_shot', labelKey: 'errors.tabNowGoal' },
];

function ErrorLog({ token, backendUrl, dateRange, setDateRange, error, setError, userRoles, visiblePages }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isRootOrAdmin = userRoles?.includes('root') || userRoles?.includes('admin');
  const availableSources = useMemo(
    () => SOURCES.filter((s) => isRootOrAdmin || (visiblePages || []).includes(s.permission)),
    [isRootOrAdmin, visiblePages],
  );

  const requestedSource = searchParams.get('source');
  const activeSource = useMemo(() => {
    if (availableSources.find((s) => s.key === requestedSource)) return requestedSource;
    return availableSources[0]?.key;
  }, [requestedSource, availableSources]);

  const activeSourceConfig = SOURCES.find((s) => s.key === activeSource);

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const pagination = usePagination('errors.pageSize', 50);
  const { sortableHeader, sortData } = useTableSort('created_at', 'desc');

  const fetchErrors = useCallback(async (signal) => {
    if (!activeSourceConfig || !token || !dateRange.start_date || !dateRange.end_date) return;
    setLoading(true);
    try {
      const skip = (pagination.currentPage - 1) * pagination.pageSize;
      const dataUrl = `${backendUrl}${activeSourceConfig.listPath}`;
      const countUrl = `${backendUrl}${activeSourceConfig.listPath}/count`;
      const commonParams = { start_date: dateRange.start_date, end_date: dateRange.end_date };
      const headers = { Authorization: `Bearer ${token}` };

      let newTotalCount = 0;
      try {
        const countRes = await axios.get(countUrl, { headers, params: commonParams, signal });
        newTotalCount = countRes.data.count || 0;
      } catch (countErr) {
        if (axios.isCancel(countErr) || countErr.name === 'CanceledError') return;
        console.warn('無法獲取總數 (後端可能未實作 /count):', countErr);
      }
      if (signal?.aborted) return;
      pagination.setTotalCount(newTotalCount);

      const newTotalPages = Math.ceil(newTotalCount / pagination.pageSize);
      if (pagination.currentPage > newTotalPages && newTotalPages > 0) {
        setErrors([]);
        setLoading(false);
        return;
      }

      const res = await axios.get(dataUrl, {
        headers,
        params: { ...commonParams, skip, limit: pagination.pageSize },
        signal,
      });
      if (signal?.aborted) return;

      const data = Array.isArray(res.data?.error) ? res.data.error : [];
      setErrors(data);
      setError(null);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      console.error(err);
      setError(err.response?.data?.detail || err.message);
      setErrors([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [backendUrl, token, activeSourceConfig, dateRange.start_date, dateRange.end_date, pagination, setError]);

  useEffect(() => {
    if (!token || !activeSourceConfig) return undefined;
    const controller = new AbortController();
    fetchErrors(controller.signal);
    return () => controller.abort();
  }, [token, activeSourceConfig, dateRange.start_date, dateRange.end_date, pagination.currentPage, pagination.pageSize, fetchErrors]);

  const autoRefresh = useAutoRefresh(fetchErrors, { defaultInterval: 60, defaultEnabled: true });

  const handleTabClick = (key) => {
    if (key === activeSource) return;
    setSearchParams({ source: key }, { replace: false });
    setSearchQuery('');
    setErrors([]);
    pagination.resetPage();
    autoRefresh.setLastUpdated(null);
  };

  const handleApply = () => {
    pagination.resetPage();
    setError(null);
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const searchFiltered = !q
      ? errors
      : errors.filter((e) =>
          String(e.title ?? '').toLowerCase().includes(q) ||
          String(e.discrption ?? '').toLowerCase().includes(q) ||
          String(e.err ?? '').toLowerCase().includes(q),
        );
    return sortData(searchFiltered, ['created_at']);
  }, [errors, searchQuery, sortData]);

  const handleShowImage = async (errorId) => {
    if (!errorId || !activeSourceConfig) return;
    const newTab = window.open('', '_blank');
    newTab.document.write(t('common.loadingScreenshot'));
    try {
      const res = await axios.get(
        `${backendUrl}${activeSourceConfig.screenshotPath}/${errorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const objectUrl = URL.createObjectURL(res.data);
      newTab.location.href = objectUrl;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      newTab.document.write(`${t('common.screenshotError')}: ${errorMsg}`);
      setError(`${t('common.screenshotError')}: ${errorId} - ${errorMsg}`);
    }
  };

  if (availableSources.length === 0) {
    return (
      <div className="container py-4">
        <h2>{t('errors.pageTitle')}</h2>
        <div className="alert alert-warning">{t('common.noData')}</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('errors.pageTitle')}</h2>
        <AutoRefreshControls {...autoRefresh} />
      </div>

      <ul className="nav nav-tabs mb-3">
        {availableSources.map((s) => (
          <li className="nav-item" key={s.key}>
            <button
              type="button"
              className={`nav-link ${activeSource === s.key ? 'active' : ''}`}
              onClick={() => handleTabClick(s.key)}
            >
              {t(s.labelKey)}
            </button>
          </li>
        ))}
      </ul>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label htmlFor="error-search" className="form-label">{t('common.search')}</label>
          <input
            id="error-search"
            className="form-control"
            placeholder={t('errors.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label htmlFor="error-start" className="form-label">{t('common.startDate')}</label>
          <input
            id="error-start"
            type="date"
            className="form-control"
            value={dateRange.start_date}
            onChange={(e) => setDateRange((prev) => ({ ...prev, start_date: e.target.value }))}
          />
        </div>
        <div className="col-auto">
          <label htmlFor="error-end" className="form-label">{t('common.endDate')}</label>
          <input
            id="error-end"
            type="date"
            className="form-control"
            value={dateRange.end_date}
            onChange={(e) => setDateRange((prev) => ({ ...prev, end_date: e.target.value }))}
          />
        </div>
        <div className="col-auto">
          <button className="btn btn-primary" onClick={handleApply} disabled={loading}>
            {t('common.apply')}
          </button>
        </div>
      </div>

      <p className="text-muted small mb-3">
        {t('common.dateRange')}: {dateRange.start_date} {t('common.to')} {dateRange.end_date} | {t('common.totalCount')}: {pagination.totalCount} | {t('common.pageCount')}: {filtered.length}
      </p>

      <PaginationControls {...pagination} pageSizes={[50, 100, 200, 500]} />

      <ErrorLoading error={error} loading={loading} />

      {!loading && (
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                {sortableHeader(t('errors.title'), 'title')}
                {sortableHeader(t('errors.description'), 'discrption')}
                <th>{t('errors.errorCol')}</th>
                {sortableHeader(t('common.createdAt'), 'created_at')}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted">{t('common.noData')}</td>
                </tr>
              )}
              {filtered.map((e, idx) => (
                <tr
                  key={e?._id || idx}
                  onClick={() => e?._id && handleShowImage(e._id)}
                  style={{ cursor: e?._id ? 'pointer' : 'default' }}
                  title={e?._id ? t('common.clickToViewScreenshot') : ''}
                >
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.title}</td>
                  <td style={{ whiteSpace: 'pre-wrap', maxWidth: '300px' }}>{e.discrption}</td>
                  <td style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9em' }}>{e.err}</td>
                  <td style={{ minWidth: '160px', whiteSpace: 'nowrap' }}>{formatDateTime(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ErrorLog;
